import { useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { TFunction } from "i18next/typescript/t";

import FilterRenderer from "@/components_v2/Filters/FilterRenderer";
import { FilterField } from "@/components_v2/Filters/types";
import {
  HookFormBaseDropdown,
  HookFormInput,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import { ApplicationStatuses } from "@/shared/utils/constants";
import {
  ApplicationFiltersProps,
  ApplicationFiltersState,
} from "@/features/organizations/profile/types";
import { useFilterCount } from "@/shared/hooks/useFilterCount";

import "../styles.css";

const applicationFiltersSchema = (
  t: TFunction,
): yup.ObjectSchema<ApplicationFiltersState> =>
  yup
    .object()
    .shape({
      search: yup
        .string()
        .transform((value) => (value === "" ? undefined : value))
        .typeError(t("organizations.validation.notValid")),
      status: yup
        .array()
        .of(
          yup
            .string()
            .required()
            .typeError(t("organizations.validation.notValid")),
        ),
    })
    .required();

const ApplicationFilters = ({
  filterNames,
  setFilters,
  defaultFilters,
}: ApplicationFiltersProps) => {
  const { t } = useTranslation();

  const form = useForm<ApplicationFiltersState>({
    resolver: yupResolver(applicationFiltersSchema(t)),
    mode: "onChange",
    defaultValues: {
      search: "",
      status: [],
      ...defaultFilters,
    },
  });

  const filterFields: Record<string, FilterField> = useMemo(() => {
    return {
      search: {
        name: "search",
        isAdvancedFilter: false,
        isDebounced: true,
        component: (
          <HookFormInput
            name="search"
            control={form.control}
            suffix={<SearchOutlined />}
            placeholder={t("organizations.placeholders.searchApplications")}
          />
        ),
      },
      status: {
        name: "status",
        label: t("organizations.labels.status"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormBaseDropdown
            name="status"
            control={form.control}
            multiple
            options={ApplicationStatuses.asList()}
            placeholder={t("organizations.placeholders.selectStatus")}
            allowClear={true}
          />
        ),
      },
    };
  }, []);

  const activeFilterCount = useFilterCount<ApplicationFiltersState>(
    form,
    defaultFilters,
  );

  return (
    <FilterRenderer
      form={form}
      filters={filterFields}
      activeFilterNames={filterNames}
      popoverTitle={t("organizations.labels.filters")}
      handleClear={() => {
        form.reset(defaultFilters);
        setFilters(form.getValues());
      }}
      onFiltersChange={setFilters}
      areFiltersDirty={form.formState.isDirty}
      activeFilterCount={activeFilterCount}
      handleSearch={() => setFilters(form.getValues())}
      className="filter-layout"
    />
  );
};

export default ApplicationFilters;
